import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, of, switchMap, tap} from 'rxjs';
import {ErrorEventEntitiesActions} from './error-event-entities.actions';
import {DatevIncometaxExportConfigService, ExportJobPageDTO} from '../../openapi/export-openapi';
import {NGXLogger} from 'ngx-logger';
import {ActionCreator} from '@ngrx/store';
import {TypedAction} from '@ngrx/store/src/models';
import {MappedHttpErrorResponse, mappedHttpErrorResponseOperator} from '@adnova/jf-ng-components';


@Injectable()
export class ErrorEventEntitiesEffects {

  private readonly _logger = inject(NGXLogger);

  private readonly _actions$ = inject(Actions);

  private readonly _exportService = inject(DatevIncometaxExportConfigService);

  /**
   * Der Effekt erlaubt asynchrones Laden von Error Events durch serverseitige Datenanfragen.
   * Dabei steuert er die Auslösung von Actions basierend auf dem Erfolg oder Misserfolg der Anfrage.
   *
   * @property onAction - Action Creator, der ausgeführt wird, um den Effekt zu starten
   * @property successAction - Action Creator, der ausgeführt wird, wenn die Daten erfolgreich vom Server abgerufen wurden
   * @property failureAction - Action Creator, der ausgeführt wird, wenn beim Abrufen der Daten ein Fehler aufgetreten ist
   *
   * @remarks
   * - Falls `updatedAt` nicht gesetzt ist, wird als Fallback `createdAt` verwendet
   * - Unterstützt Pagination über `pageIndex` und `pageSize` Parameter
   */
  readonly loadErrorEvents$ = (
    onAction: ActionCreator<any, (props: {
      exportConfigId: string;
      pageIndex: number;
      pageSize: number;
    }) => ({
      exportConfigId: string;
      pageIndex: number;
      pageSize: number;
    } & TypedAction<any>)>,
    successAction?: ActionCreator<any, (props: {
      page: ExportJobPageDTO;
      exportConfigId: string;
    }) => ({
      page: ExportJobPageDTO;
      exportConfigId: string;
    } & TypedAction<any>)>,
    failureAction?: ActionCreator<any, (props: {
      error: MappedHttpErrorResponse;
      exportConfigId: string;
    }) => ({
      error: MappedHttpErrorResponse;
      exportConfigId: string;
    } & TypedAction<any>)>,
  ) => createEffect(
    () => this._actions$.pipe(
      ofType(onAction),
      switchMap(({exportConfigId, pageIndex, pageSize}) => {
        return this._exportService.getExportConfigJobs(
          exportConfigId,
          {
            number: pageIndex,
            size: pageSize,
          }
        ).pipe(
          switchMap(page => {
            // Fallback: Wenn updatedAt nicht gesetzt ist, verwende createdAt
            page.content.forEach(job => {
              if (!job.updatedAt && job.createdAt) {
                job.updatedAt = job.createdAt;
              }
            });

            return [
              ErrorEventEntitiesActions.loadErrorEventsSuccess({
                page,
                exportConfigId,
              }),
              ...(successAction ? [successAction({
                page,
                exportConfigId,
              })] : []),
            ];
          }),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            switchMap(error => {
              return [
                ErrorEventEntitiesActions.loadErrorEventsFailure({error, exportConfigId}),
                ...(failureAction ? [failureAction({error, exportConfigId})] : []),
              ];
            }),
          )),
        );
      }),
    )
  );

  /**
   * Loggt erfolgreiche Ladevorgänge von Error Events.
   *
   * Dieser Effekt reagiert auf die `loadErrorEventsSuccess` Action und schreibt
   * eine Debug-Meldung mit den geladenen Export Jobs in das Log.
   */
  readonly loadErrorEventsSuccess$ = createEffect(
    () => this._actions$.pipe(
      ofType(ErrorEventEntitiesActions.loadErrorEventsSuccess),
      tap(({page, exportConfigId}) => {
        this._logger.debug('Error events retrieved successfully for config:', exportConfigId, {
          count: page.content.length,
          totalElements: page.totalElements,
        });
      })
    ),
    {dispatch: false}
  );

  /**
   * Loggt Fehler beim Laden von Error Events.
   *
   * Dieser Effekt reagiert auf die `loadErrorEventsFailure` Action und schreibt
   * eine Fehlermeldung in das Log.
   */
  readonly loadErrorEventsFailure$ = createEffect(
    () => this._actions$.pipe(
      ofType(ErrorEventEntitiesActions.loadErrorEventsFailure),
      tap(({error}) => {
        this._logger.error('Error retrieving error events:', error);
      })
    ),
    {dispatch: false}
  );

}
